import { exportXlsx } from '@/shared/utils/exportXlsx'

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function cellText(row, key) {
  const cells = row?.cells || row || {}
  const val = cells[key]
  if (val == null) return ''
  if (typeof val === 'object') return val.display || val.timestamp || ''
  return String(val)
}

/** Boot columns use `key`; IndexColumnFilters meta expects `name`. */
export function novMetaColumns(columns = []) {
  return (columns || []).map((col) => ({
    name: col.key || col.name,
    label: col.label || col.title,
    type: col.type || 'string',
    no_search: col.no_search || false,
  }))
}

/**
 * Spread `cells` onto the row so Vuetify can sort by header `key`.
 * Keeps `cells` for templates that still read `item.cells[key]`.
 */
export function flattenNovRows(rows = []) {
  return (rows || []).map((row) => {
    if (!row?.cells || typeof row.cells !== 'object') return row
    return { ...row, ...row.cells }
  })
}

/**
 * Client-side column + quick search for NOV rows shaped as { cells: { key: value } }
 * or flat objects (audit rows). Always returns flattened rows for table sort.
 */
export function filterNovRows(rows = [], { quickSearch = '', columnFilters = {}, cellKeys = null } = {}) {
  const q = String(quickSearch || '').trim().toLowerCase()
  const active = Object.entries(columnFilters || {}).filter(
    ([, v]) => v != null && String(v).trim() !== '',
  )

  const filtered = (rows || []).filter((row) => {
    if (active.length) {
      const ok = active.every(([name, raw]) => {
        const needle = String(raw).trim().toLowerCase()
        if (!needle) return true
        const source = row?.cells && typeof row.cells === 'object' ? { ...row, ...row.cells } : row
        const val = source?.[name]
        if (val == null) return false
        const text =
          typeof val === 'object'
            ? String(val.display ?? val.text ?? val.title ?? '')
            : String(val)
        const dateMatch = dateFilterMatches(needle, text, source, name)
        if (dateMatch !== null) return dateMatch
        return text.toLowerCase().includes(needle)
      })
      if (!ok) return false
    }

    if (!q) return true
    if (row?.cells) {
      return Object.values(row.cells).some((v) => {
        if (v && typeof v === 'object') {
          return String(v.display ?? v.text ?? v.title ?? '').toLowerCase().includes(q)
        }
        return String(v ?? '').toLowerCase().includes(q)
      })
    }
    const keys = cellKeys || Object.keys(row || {}).filter((k) => k !== 'id' && k !== 'href')
    return keys.some((k) => {
      const v = row?.[k]
      if (v && typeof v === 'object') {
        return String(v.display ?? v.text ?? v.title ?? v.html ?? '')
          .replace(/<[^>]+>/g, ' ')
          .toLowerCase()
          .includes(q)
      }
      return String(v ?? '').toLowerCase().includes(q)
    })
  })

  return flattenNovRows(filtered)
}

/**
 * Date fields use an ISO value from Flatpickr while audit rows usually display
 * a localized timestamp. Compare both representations in the browser's local
 * time so date and date-time column filters work consistently.
 */
function dateFilterMatches(needle, text, source, name) {
  const dateTime = /^(\d{4}-\d{2}-\d{2})\s+(\d{2}):(\d{2})$/.exec(needle)
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(needle)
  if (!dateTime && !dateOnly) return null

  const iso =
    source?.[`${name}Iso`] ??
    source?.[`${name}_iso`] ??
    source?.[`${name}Day`] ??
    null
  if (iso != null && String(iso).trim() !== '') {
    const normalized = String(iso).slice(0, dateTime ? 16 : 10).replace('T', ' ')
    if (normalized === needle) return true
  }

  if (text.toLowerCase().includes(needle)) return true
  const parsed = Date.parse(text)
  if (Number.isNaN(parsed)) return false

  const d = new Date(parsed)
  const localDate = [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0')
  ].join('-')
  if (dateOnly) return localDate === needle

  const localDateTime = `${localDate} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  return localDateTime === `${dateTime[1]} ${dateTime[2]}:${dateTime[3]}`
}

/** Classic DataTables Buttons–style copy confirmation. */
export function showCopyFeedback(rowCount) {
  const existing = document.getElementById('blv-export-copy-info')
  if (existing) existing.remove()

  const message =
    rowCount === 1
      ? 'Copied 1 row to the clipboard'
      : `Copied ${rowCount} rows to the clipboard`

  const el = document.createElement('div')
  el.id = 'blv-export-copy-info'
  el.setAttribute('role', 'status')
  el.innerHTML = `<h2>Copy to clipboard</h2><div>${message}</div>`
  Object.assign(el.style, {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: '20000',
    background: 'rgba(0, 0, 0, 0.75)',
    color: '#fff',
    borderRadius: '4px',
    padding: '1.5em 2em',
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(0,0,0,.35)',
    fontFamily: "'Source Sans 3', 'Segoe UI', sans-serif",
    cursor: 'pointer',
  })
  const title = el.querySelector('h2')
  if (title) {
    Object.assign(title.style, {
      margin: '0 0 0.5em',
      fontSize: '1.25em',
      fontWeight: '600',
      color: '#fff',
    })
  }

  const dismiss = () => {
    el.remove()
    clearTimeout(timer)
  }
  el.addEventListener('click', dismiss)
  document.body.appendChild(el)
  const timer = setTimeout(dismiss, 2000)
}

export function exportCopy({ headers, rows, getCell = cellText }) {
  const lines = [
    headers.map((c) => c.title).join('\t'),
    ...rows.map((row) => headers.map((c) => getCell(row, c.key)).join('\t')),
  ]
  const text = lines.join('\n')
  const count = rows.length

  // On http://LAN-IP (not localhost), Clipboard API is unavailable or fails
  // after the click gesture — use sync execCommand so paste into Word works.
  if (!window.isSecureContext || !navigator.clipboard?.writeText) {
    if (fallbackCopy(text)) showCopyFeedback(count)
    return
  }

  navigator.clipboard.writeText(text).then(
    () => showCopyFeedback(count),
    () => {
      if (fallbackCopy(text)) showCopyFeedback(count)
    },
  )
}

function fallbackCopy(text) {
  const ta = document.createElement('textarea')
  ta.value = text
  ta.setAttribute('readonly', '')
  // Keep in-viewport but invisible so browsers still allow execCommand('copy')
  Object.assign(ta.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '1px',
    height: '1px',
    padding: '0',
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
    background: 'transparent',
    opacity: '0',
  })
  document.body.appendChild(ta)

  let ok = false
  try {
    ta.focus()
    ta.select()
    ta.setSelectionRange(0, text.length)
    ok = document.execCommand('copy')
  } catch {
    ok = false
  } finally {
    ta.remove()
  }
  return ok
}

export function exportExcel({ title, headers, rows, filename = 'export.xlsx', getCell = cellText }) {
  return exportXlsx({ title, headers, rows, filename, getCell })
}

function plainText(value) {
  return String(value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

function buildPrintHtml({ title, headers, rows, getCell = cellText }) {
  const wide = (headers || []).length > 4
  const body = (rows || [])
    .map(
      (row) =>
        `<tr>${headers.map((c) => `<td>${escapeHtml(getCell(row, c.key))}</td>`).join('')}</tr>`,
    )
    .join('')
  return `<!DOCTYPE html><html><head><title>${escapeHtml(title)}</title>
    <style>
      @page{size:${wide ? 'landscape' : 'portrait'};margin:12mm}
      body{font:${wide ? '9px' : '12px'}/1.35 'Source Sans 3',sans-serif;color:#1b242c;margin:16px}
      h2{font-size:16px;margin:0 0 10px;color:#0B7EA4}
      table{border-collapse:collapse;width:100%;table-layout:fixed}
      th,td{border:1px solid #d7dee5;padding:4px 6px;text-align:left;word-wrap:break-word;overflow-wrap:anywhere;vertical-align:top}
      th{background:#e8f4f8;color:#0a4f6c;text-transform:uppercase;font-size:${wide ? '8px' : '11px'}}
    </style></head><body>
    <h2>${escapeHtml(title)}</h2>
    <table><thead><tr>${headers.map((c) => `<th>${escapeHtml(c.title || c.label || c.key)}</th>`).join('')}</tr></thead>
    <tbody>${body}</tbody></table></body></html>`
}

/** Print via hidden iframe — avoids blank tab / "pagebreak" popup window. */
export function exportPrint({ title, headers, rows, getCell = cellText }) {
  const html = buildPrintHtml({ title, headers, rows, getCell })
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none'
  document.body.appendChild(frame)

  let cleanupTimer
  const cleanup = () => {
    clearTimeout(cleanupTimer)
    frame.remove()
  }

  try {
    const doc = frame.contentDocument || frame.contentWindow?.document
    if (!doc) throw new Error('Could not create print document')
    doc.open()
    doc.write(html)
    doc.close()
    frame.contentWindow?.addEventListener('afterprint', cleanup, { once: true })
    cleanupTimer = setTimeout(cleanup, 60000)
    setTimeout(() => {
      try {
        frame.contentWindow?.focus()
        frame.contentWindow?.print()
      } catch {
        cleanup()
      }
    }, 150)
  } catch (e) {
    cleanup()
    console.error('Print export failed', e)
  }
}

export function exportPdf({ title, headers, rows, filename = 'export.pdf', getCell = cellText }) {
  const cols = headers || []
  const pdfMake = window.pdfMake
  if (!pdfMake?.createPdf || !pdfMake.vfs) {
    // Fallback when pdfMake/fonts are not on the page (classic DataTables loads them).
    // Use iframe print — never open a blank tab.
    exportPrint({ title, headers: cols, rows, getCell })
    return
  }

  const wide = cols.length > 4
  const fontSize = wide ? 7 : 9
  const body = [
    cols.map((c) => ({
      text: plainText(c.title || c.label || c.key),
      style: 'tableHeader',
      noWrap: false,
    })),
    ...(rows || []).map((row) =>
      cols.map((c) => ({
        text: plainText(getCell(row, c.key)),
        style: 'tableBody',
        noWrap: false,
      })),
    ),
  ]

  try {
    pdfMake
      .createPdf({
        pageSize: 'LETTER',
        pageOrientation: wide ? 'landscape' : 'portrait',
        pageMargins: wide ? [18, 24, 18, 24] : [30, 40, 30, 40],
        content: [
          { text: plainText(title) || 'Export', style: 'title' },
          {
            table: { headerRows: 1, widths: cols.map(() => '*'), body },
            layout: {
              fillColor: (rowIndex) =>
                rowIndex === 0 ? '#0B7EA4' : rowIndex % 2 === 0 ? '#f4f8fa' : null,
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => '#d7dee5',
              vLineColor: () => '#d7dee5',
              paddingLeft: () => 3,
              paddingRight: () => 3,
              paddingTop: () => 2,
              paddingBottom: () => 2,
            },
          },
        ],
        styles: {
          title: { fontSize: 12, bold: true, color: '#0a4f6c', margin: [0, 0, 0, 10] },
          tableHeader: { bold: true, fontSize, color: '#ffffff' },
          tableBody: { fontSize, color: '#1b242c' },
        },
        defaultStyle: { fontSize },
      })
      .download(filename || 'export.pdf')
  } catch (e) {
    console.error('PDF export failed; falling back to print', e)
    exportPrint({ title, headers: cols, rows, getCell })
  }
}
